import { Grid, Typography } from '@mui/material';
import { getBusinessTgrProceedings } from 'lib/api';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import ToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { showAlert } from 'lib/functions/utils';
import { BusinessProceeding, BusinessProceedingFolio } from 'lib/types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

const TGRProceedings: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [allProceedings, setAllProceedings] = useState<BusinessProceeding[]>(
    [],
  );
  const [filteredProceedings, setFilteredProceedings] = useState<
    BusinessProceeding[]
  >([]);
  const [allFolios, setAllFolios] = useState<BusinessProceedingFolio[]>([]);
  const [filteredFolios, setFilteredFolios] = useState<
    BusinessProceedingFolio[]
  >([]);
  const [selectProceeding, setSelectedProceeding] =
    useState<BusinessProceeding | null>(null);
  const [filter, setFilter] = useState<'active' | 'all'>('active');

  const { id } = useParams<{ id: string }>();

  const getActiveFolios = (
    folios: BusinessProceedingFolio[],
    proceedings: BusinessProceeding[],
  ) => {
    const activeFolios: BusinessProceedingFolio[] = [];
    // Only add folios that are associated with active proceedings
    folios.forEach((folio: BusinessProceedingFolio) => {
      if (
        proceedings.find(
          (proceeding: BusinessProceeding) =>
            proceeding.id === folio.tgrProceedingId && proceeding.active,
        )
      ) {
        activeFolios.push(folio);
      }
    });
    return activeFolios;
  };

  const fetchRelations = async () => {
    if (!id) return;
    try {
      const tgrData = await getBusinessTgrProceedings(dispatch, id);
      if (!tgrData) {
        setLoading(false);
        return showAlert(dispatch, 'error', 'Error al obtener los expedientes');
      }
      const activeFolios = getActiveFolios(
        tgrData.businessProceedingFolios,
        tgrData.businessProceedings,
      );
      setAllFolios([...tgrData.businessProceedingFolios]);
      setFilteredFolios([...activeFolios]);
      setAllProceedings([...tgrData.businessProceedings]);
      setFilteredProceedings([
        ...tgrData.businessProceedings.filter(
          (proceeding: BusinessProceeding) => proceeding.active,
        ),
      ]);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener los expedientes');
    } finally {
      setLoading(false);
    }
  };

  const handleClickRowProceeding = (id: string) => {
    const foundAgreement = allProceedings.find(
      (agreement: BusinessProceeding) => agreement.id === id,
    );
    if (!foundAgreement) return;

    if (selectProceeding && selectProceeding.id === id) {
      setSelectedProceeding(null);
      if (filter === 'active') {
        return setFilteredFolios(getActiveFolios(allFolios, allProceedings));
      }
      return setFilteredFolios(allFolios);
    }
    const newCuotas = allFolios.filter(
      (cuota: BusinessProceedingFolio) => cuota.tgrProceedingId === id,
    );
    setFilteredFolios(newCuotas);
    setSelectedProceeding(foundAgreement);
  };

  const handleFilterChange = (value: 'active' | 'all') => {
    setFilter(value);
    if (value === 'active') {
      setFilteredProceedings(
        allProceedings.filter(
          (proceeding: BusinessProceeding) => proceeding.active,
        ),
      );
      setFilteredFolios(getActiveFolios(allFolios, allProceedings));
      setSelectedProceeding(null);
    } else {
      setFilteredProceedings(allProceedings);
      setFilteredFolios(allFolios);
      setSelectedProceeding(null);
    }
  };

  useEffect(() => {
    void fetchRelations();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">
            Expedientes {filter === 'active' ? 'vigentes' : 'históricos'}
          </Typography>
          <ToggleButtonGroup
            options={[
              { label: 'Vigentes', value: 'active' },
              { label: 'Históricos', value: 'all' },
            ]}
            value={filter}
            onChange={(value) => handleFilterChange(value as 'active' | 'all')}
          />
        </div>
        <EnhancedTable
          finalSumRow
          exportToFile="xlsx"
          tableSize="small"
          dataHeaders={[
            { key: 'proceedingId', label: 'Expediente' },
            { key: 'year', label: 'Año' },
            { key: 'commune', label: 'Comuna' },
            { key: 'status', label: 'Etapa' },
            { key: 'amount', label: 'Monto liquidado', type: 'money' },
            { key: 'active', label: 'Activo', type: 'boolean-active-null' },
            { key: 'updatedAt', label: 'Cambio de estado', type: 'date' },
          ]}
          data={filteredProceedings}
          handleClickRow={handleClickRowProceeding}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">Folios</Typography>
        <EnhancedTable
          exportToFile="xlsx"
          finalSumRow
          tableSize="small"
          dataHeaders={[
            { key: 'folio', label: 'Folio' },
            { key: 'proceedingId', label: 'Expediente' },
            { key: 'amount', label: 'Monto', type: 'money' },
            { key: 'dueDate', label: 'Fecha de vencimiento', type: 'date' },
          ]}
          data={filteredFolios}
          lastElement
        />
      </Grid>
    </Grid>
  );
};

export default TGRProceedings;
